import { Box, Button, CardMedia, Typography } from "@mui/material";
import numeral from "numeral";
import React, { useEffect, useRef, useContext, useState } from "react";
import BarChartComponent from "./BarChartComponent";
import * as styles from "../styles/MoreFinancial.js";
import { PreciosContext } from "../hooks/PricesContext";
import Tag from "./Tag.js";
import MyChartComponent from "./LineChart.js";
import MixedChart from "./MixedChart.js";
import MyChart from "./MyChart.js";
import { BusAlertTwoTone } from "@mui/icons-material";

const MoreFinancial = ({ image, stock, setFront }) => {
  const textStyle = {
    fontSize: { xs: "1.94vh", md: "2.2vh", lg: "2.3vh" },
    marginLeft: { xs: "2px", md: "10px" },
    marginRight: { xs: "2px", md: "10px" },
    fontWeight: "bold",
  };
  const webStyle = {
    fontSize: { xs: "1.94vh", md: "2vh", lg: "2.3vh" },
    marginRight: { xs: "2px", md: "10px" },
    marginTop: { xs: "2px", md: "10px" },
  };

  const disablePageScroll = () => {
    document.body.style.overflow = "hidden";
  };

  // Función para restaurar el scroll de la página
  const enablePageScroll = () => {
    document.body.style.overflow = "auto";
  };

  // Se deshabilita el scroll al montar el componente
  useEffect(() => {
    disablePageScroll();
    return () => {
      // Se restaura el scroll al desmontar el componente
      enablePageScroll();
    };
  }, []);

  const containerRef = useRef(null);

  const scrollToComponent = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      }); // Center the component in the viewport
    }
  };

  useEffect(() => {
    scrollToComponent();
  }, []);

  const variacion = ((stock.actual_price - stock.price) / stock.price) * 100;
  const { dolarCCL } = useContext(PreciosContext);

  const Diff = ((stock.target - stock.price) / stock.price) * 100;

  const data = [
    { label: "Objetivo", value: stock.target },
    {
      label: "Upside/Downside",
      value: Diff.toFixed(2) + "%",
      color: Diff * -1 <= 0 ? "green" : "red",
    },
    {
      label: "Riesgo",
      value: "",
      tag: {
        text: stock.riesgo,
        colorChoice:
        stock.riesgo === "Bajo" ? "green" : stock.riesgo === "Medio" ? "orange" : "red",
      },
    },
    {
      label: "Deuda",
      value: "",
      tag: {
        text: stock.deuda,
        colorChoice:
        stock.deuda === "Baja" ? "green" : stock.deuda === "Media" ? "orange" : "red",
      },
    },
    {
      label: "Crecimiento",
      value: "",
      tag: {
        text: stock.crecimiento,
        colorChoice:
        stock.crecimiento === "Alto"
            ? "green"
            : stock.crecimiento === "Medio"
            ? "orange"
            : "red",
      },
    },
    {
      label: "Margenes",
      value: "",
      tag: {
        text: stock.margenes,
        colorChoice:
        stock.margenes === "Altos"
            ? "green"
            : stock.margenes === "Medios"
            ? "orange"
            : "red",
      },
    },
  ];

  const dataMobile = data.filter((item) =>
    ["Precio", "Objetivo", "Upside/Downside"].includes(item.label)
  );
  const dataMobile2 = data.filter((item) =>
    ["Riesgo", "Deuda", "Crecimiento", "Margenes"].includes(item.label)
  );

  const [mostrarDescripcion, setMostrarDescripcion] = useState(false);

  const handleMostrarDescripcion = () => {
    setMostrarDescripcion(!mostrarDescripcion);
  };

  const CustomBox = ({ data, sx }) => {
    return (
      <Box
        sx={{
          ...styles.containerDatos,
          ...sx,
        }}
      >
        {data.map((item) => (
          <Box
            key={item.label}
            sx={{
              ...styles.boxPriceFullScreen,
            }}
          >
            <Typography sx={styles.priceStyle}>{item.label}</Typography>
            {item.tag && (
              <Tag
                text={item.tag.text}
                colorChoice={item.tag.colorChoice}
                sx={styles.priceStyle}
              />
            )}
            {item.value && (
              <Typography
                sx={{
                  ...styles.priceStyle,
                  ...(item.label === "Upside/Downside" ? { color: item.color } : {}),
                }}
              >
                ${item.value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };
  
  return (
    <Box ref={containerRef} sx={styles.containerStyle}>
      {/* Nombre, Precio, Variacion, Capitalizacion */}
      <Box sx={styles.boxMobileStyle}>
        <Box sx={styles.boxCardMedia2}>
          <CardMedia height="100%" width="100%" component="img" image={image} />
        </Box>
        <Button
          variant="contained"
          sx={styles.volverButton2}
          onClick={() => setFront(true)}
        >
          <Typography sx={styles.volverText}>Volver</Typography>
        </Button>
      </Box>
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "row",
          justifyContent: "space-around",
          marginTop: "18px",
          marginBottom: "0px",
          paddingBottom: "10px",
        }}
      >
        <Button
          variant="contained"
          sx={styles.volverButton}
          onClick={() => setFront(true)}
        >
          <Typography sx={styles.volverText}>Volver</Typography>
        </Button>
        <Typography sx={styles.nameText}>{stock.name}</Typography>
        <Typography
          sx={styles.priceText}
          variant="h5"
        >{`Precio: $${stock.actual_price}`}</Typography>
        <Typography sx={styles.variacionText} variant="h5">
          {variacion !== null ? (
            <>
              Variacion:{" "}
              <span style={{ color: variacion < 0 ? "red" : "#22eb17" }}>
                {variacion.toFixed(2)}%
              </span>
            </>
          ) : (
            "Cargando variacion..."
          )}
        </Typography>
        <Typography sx={styles.capText}>
          {stock.price * stock.Acciones * 1000000 > 20000000000
            ? `Mkt Cap: $${numeral(
                (stock.price * stock.Acciones) / 1000
              ).format("0,0")} B`
            : `Capitalizacion: $${numeral(
                (stock.price * stock.Acciones) / 1000
              ).format("0,0.00")} billions`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "row",
          justifyContent: "space-around",
          marginBottom: "0px",
          paddingBottom: "10px",
        }}
      >
        <Typography sx={styles.priceText2} variant="h5">
          {stock.price !== null
            ? `Precio: $${stock.price}`
            : "Cargando precio..."}
        </Typography>
        <Typography sx={styles.variacionText2} variant="h5">
          {variacion !== null ? (
            <>
              Var:{" "}
              <span style={{ color: variacion < 0 ? "red" : "#22eb17" }}>
                {variacion.toFixed(2)}%
              </span>
            </>
          ) : (
            "Cargando variacion..."
          )}
        </Typography>
      </Box>
      <Box sx={styles.separadorStyle}></Box>
      {/* Logo y Descripcion */}
      <Box sx={styles.box2Financial}>
        <Box sx={styles.boxCardMedia}>
          <CardMedia
            height="100%"
            width="100%"
            component="img"
            image={image}
            paddingLeft={{ xs: 0, md: "8vh" }}
          />
        </Box>
        {/* Descripcion + Website */}
        <Box sx={{ width: { xs: "100%", md: "70%" }, display:"none"}}>
          <Typography sx={styles.descriptionText}>
            {stock.description}
          </Typography>
          <Box
            sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
          >
            <Typography sx={webStyle}>Website:</Typography>
            <Button
              onClick={() => window.open(stock.web)}
              sx={{
                textTransform: "none",
                color: "blue",
                textDecoration: "underline",
                padding: 0,
              }}
            >
              <Typography sx={webStyle}>{stock.web}</Typography>
            </Button>
          </Box>
        </Box>
        <Box sx={styles.boxContenedorAnalisis}>
          {mostrarDescripcion ? (
            <Button onClick={handleMostrarDescripcion}>
              <Typography>Volver</Typography>
            </Button>
          ) : (
            <Button onClick={handleMostrarDescripcion}>
              <Typography>Descripcion</Typography>
            </Button>
          )}
          {!mostrarDescripcion && (
            <>
              <CustomBox
                data={dataMobile}
                sx={{ display: { xs: "flex", sm: "none" } }}
              />
              <CustomBox
                data={dataMobile2}
                sx={{ display: { xs: "flex", sm: "none" } }}
              />
              <CustomBox
                data={data}
                sx={{ display: { xs: "none", sm: "flex" } }}
              />
            </>
          )}
          {mostrarDescripcion && (
            <Typography sx={styles.descriptionText}>{stock.description}</Typography>
          )}
        </Box>
        {/* Analisis Card */}
      </Box>
      {/* <Box sx={styles.separadorStyle}></Box> */}
      {/* Box Ratios de Conversion */}
      <Box
        sx={{
          display: "none",// Aca display flex
          flexDirection: { xs: "column", md: "row" },
          paddingTop: "5px",
          paddingBottom: "10px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              marginBottom: { xs: "10px", md: 0 },
              marginTop: { xs: "10px", md: 0 },
              paddingRight: { xs: "20px" },
            }}
          >
            <Typography sx={textStyle}>Ratio Conversion:</Typography>
            <Typography sx={textStyle}>{stock.ratio}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              marginBottom: { xs: "10px", md: 0 },
              marginTop: { xs: "10px", md: 0 },
              paddingRight: { xs: "20px", md: 0 },
            }}
          >
            <Typography sx={textStyle}>Precio USD:</Typography>
            <Typography sx={textStyle}>
              ${(stock.price / stock.ratio).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              paddingRight: { xs: "20px" },
            }}
          >
            <Typography sx={textStyle}>Dolar CCL:</Typography>
            <Typography sx={textStyle}>${dolarCCL}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              marginRight: { xs: 0, md: "8vh" },
            }}
          >
            <Typography sx={textStyle}>Precio ARS:</Typography>
            <Typography sx={textStyle}>
              $
              {((stock.price / stock.ratio) * dolarCCL).toLocaleString(
                "es-ES",
                {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.separadorStyle}></Box>
      {/* Grafico y Ratios */}
      <Box sx={styles.box3Financial}>
        {/* Grafico */}
        <Box sx={styles.boxChart}>
          {/* <BarChartComponent stock={stock}></BarChartComponent> */}
          <MyChart stock={stock}></MyChart>
        </Box>
        <Box sx={styles.separador2Style}></Box>
        {/* Ratios */}
        <Box
          sx={{
            width: { xs: "100%", md: "45%" },
            display: "flex",
            flexDirection: "column",
            alignContent: "space-between",
            justifyContent: "center",
            marginTop: "0px",
            marginRight: { md: "30px" },
          }}
        >
          <Box>
            <Typography sx={styles.ratiosText}>Ratios de Valuacion</Typography>
          </Box>
          <Box sx={styles.box2Ratios}>
            <Box sx={{ width: "50%" }}>
              <Typography sx={styles.financialInfo2}>{`PER: ${(
                (stock.price * stock.Acciones) /
                stock.NetIncome
              ).toFixed(2)}`}</Typography>
              <Typography sx={styles.financialInfo2}>{`EV/EBITDA: ${(
                (stock.price * stock.Acciones +
                  stock.Debt -
                  stock.CashandEquiv) /
                (stock.OpIncome + stock.DandA)
              ).toFixed(2)}`}</Typography>
              <Typography sx={styles.financialInfo2}>{`EV/FCF: ${(
                (stock.price * stock.Acciones +
                  stock.Debt -
                  stock.CashandEquiv) /
                (stock.NetIncome + stock.DandA - stock.CAPEX)
              ).toFixed(2)}`}</Typography>
            </Box>
            <Box sx={{ width: "50%" }}>
              <Typography sx={styles.financialInfo2}>{`P/Sales: ${(
                (stock.price * stock.Acciones) /
                stock.Revenue
              ).toFixed(2)}`}</Typography>
              <Typography sx={styles.financialInfo2}>{`Mg Operativo: ${(
                (stock.OpIncome / stock.Revenue) *
                100
              ).toFixed(2)}%`}</Typography>
              <Typography sx={styles.financialInfo2}>{`Mg Neto: ${(
                (stock.NetIncome / stock.Revenue) *
                100
              ).toFixed(2)}%`}</Typography>
            </Box>
          </Box>
        </Box>
        {/* Prueba Grafico */}
        <Box sx={{display:"none", alignItems:"center", justifyContent:"center", alignContent:"center", justifyItems:"center", ml:"20px", mr:"20px"}}>
          {/* <MyChartComponent stock={stock}></MyChartComponent> */}
          {/* <MixedChart stock={stock}></MixedChart> */}
          {/* <MixedChart2 stock={stock}></MixedChart2> */}
        </Box>
      </Box>
    </Box>
  );
};

export default MoreFinancial;
